import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import {
  infiniteFindByQueryCacheKey,
  OrderByInput,
  SortDirectionEnum,
  IssueType,
  Severity,
  useInfiniteFindByQuery,
} from '../../graphql';
import {
  ActivityFilterKey,
  AssetChatFeedItem,
  AssetChatFeedPage,
  AssetChatSubject,
  AssetChatStreamFilter,
  getActivityFilter,
} from '../domain';
import { useActivityListFilter } from './useActivityListFilter';

export const ASSET_CHAT_PAGE_SIZE = 50;

/**
 * Pinned regardless of the persisted `ActivityFilterModel.orderBy`. Its
 * default, MostRecentUpdated, sorts on the mutable `updatedAt`, so a reply
 * would move an activity to the bottom of the chat; `effectiveCreationDate`
 * (`adjustCreationDate ?? createdAt`) is the stable chronological order a
 * conversation wants. Desc plus an inverted list gives newest-at-bottom and
 * pages into older history with no reversal.
 */
export const ASSET_CHAT_ORDER_BY: OrderByInput = {
  field: 'effectiveCreationDate',
  order: SortDirectionEnum.Desc,
};

export type UseAssetChatFeedArgs = Pick<
  AssetChatSubject,
  'workspaceAssetId' | 'assetId' | 'workspaceId'
> & {
  filterKey: ActivityFilterKey;
  streamFilter?: AssetChatStreamFilter;
  pageSize?: number;
  /**
   * Opt out of firing the query even once the persisted filter resolves —
   * for a caller (the chat icon's unread badge) that has to call this hook
   * unconditionally but only wants the request once its own subject
   * resolution is done. Defaults to true; ANDed with the existing
   * `filter !== undefined` gate, never replacing it.
   */
  enabled?: boolean;
};

/**
 * The paged `findIssues` stream behind one WorkspaceAsset's chat. The
 * persisted filter supplies scope, labels, keyword and date range; stream
 * selection narrows content type or replaces severity. Its `orderBy` is
 * ignored (see ASSET_CHAT_ORDER_BY) and the
 * workspace is pinned with `workspaceOnly`, so the server does not widen the
 * feed to the viewer's own, granted or assigned activity on the asset in
 * other workspaces.
 */
export const useAssetChatFeed = ({
  workspaceAssetId,
  assetId,
  workspaceId,
  filterKey,
  streamFilter = 'all',
  pageSize = ASSET_CHAT_PAGE_SIZE,
  enabled = true,
}: UseAssetChatFeedArgs) => {
  const queryClient = useQueryClient();
  const { data: filterModel, isLoading: isFilterLoading } =
    useActivityListFilter(filterKey);

  const filter = useMemo(() => {
    if (!filterModel) return undefined;
    return {
      ...getActivityFilter(filterModel, workspaceId, assetId),
      ...getStreamFilter(streamFilter),
      workspaceOnly: true,
    };
  }, [filterModel, workspaceId, assetId, streamFilter]);

  const cacheKey = useMemo(
    () =>
      infiniteFindByQueryCacheKey({
        modelName: 'issue',
        filter,
        orderBy: ASSET_CHAT_ORDER_BY,
        pageSize,
      }),
    [filter, pageSize]
  );
  const previousStream = useRef(streamFilter);
  const isChangingStream = previousStream.current !== streamFilter;
  useEffect(() => {
    if (previousStream.current === streamFilter) return;
    previousStream.current = streamFilter;
    // A revisited selection may still have several cached pages. Reset the
    // target query so clearing/changing the filter starts at offset zero too.
    queryClient.resetQueries({ queryKey: cacheKey, exact: true });
  }, [streamFilter, cacheKey, queryClient]);

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteFindByQuery<InfiniteData<AssetChatFeedPage>, Error>({
    query: FIND_ASSET_CHAT_FEED,
    modelName: 'issue',
    dataPrefix: 'issue.findIssues',
    filter,
    orderBy: ASSET_CHAT_ORDER_BY,
    pageSize,
    keepPreviousData: false,
    // No request until the persisted filter is known; firing on the default
    // model first would fetch, then refetch under a different cache key.
    enabled: enabled && filter !== undefined,
  });

  // Deduplicated by id, not a plain flatMap. Two things put the same Issue on
  // two pages at once: an optimistic row written into page 0 that the next
  // refetch also returns, and any new activity arriving between page fetches,
  // which shifts every later row down one server-side offset. A repeated id
  // in a FlatList is a hard React key collision, so the reader can't be left
  // to handle it. First occurrence wins — page 0 is the newest end.
  const items = useMemo(() => {
    const seen = new Set<string>();
    const deduped: AssetChatFeedItem[] = [];
    if (isChangingStream) return deduped;
    data?.pages.forEach((page) => {
      page.items?.forEach((item) => {
        if (seen.has(item.id)) return;
        seen.add(item.id);
        deduped.push(item);
      });
    });
    return deduped;
  }, [data, isChangingStream]);

  return {
    subject: { workspaceAssetId, assetId, workspaceId },
    items,
    cacheKey,
    count: data?.pages[0]?.count,
    filter,
    orderBy: ASSET_CHAT_ORDER_BY,
    isLoading: isFilterLoading || isLoading || isChangingStream,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  };
};

const getStreamFilter = (selection: AssetChatStreamFilter) => {
  switch (selection) {
    case 'messages':
      return { activityTypes: [IssueType.Note] };
    case 'activities':
      return { excludeActivityTypes: [IssueType.Note] };
    case 'urgent':
      return { severity: [Severity.Red] };
    case 'pending':
      return { severity: [Severity.Yellow] };
    case 'solved':
      return { severity: [Severity.Green] };
    case 'all':
      return {};
  }
};

export const FIND_ASSET_CHAT_FEED = `
  query FindAssetChatFeed($filter: FindIssuesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    issue {
      findIssues(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        offset
        limit
        items {
          id
          severity
          type
          title
          subtitle
          isPinned
          asset {
            id
            name
            coverImage {
              id
              url
              extension
              status
              type
            }
          }
          workspace {
            id
            name
          }
          labels {
            id
            name
          }
          createdAt
          effectiveCreationDate
          createdBy {
            id
            name {
              first
              last
            }
          }
          updatedAt
          updatedBy {
            id
            name {
              first
              last
            }
          }
          userMentions {
            id
            name {
              first
              last
            }
          }
          originMessage {
            id
            text
            i18n
            type
            createdBy {
              id
              name {
                first
                last
              }
            }
            media {
              id
              name
              type
              extension
              status
              url
            }
          }
          replyCount
        }
      }
    }
  }
`;
